/**
 * Association Utilities
 * Handles multi-entity associations for tasks and notes
 * Enables cross-referencing between customers, opportunities, orders, contacts, and tasks
 * 
 * @module associationUtils
 */

import { getDoc, doc, setDoc, getDocs, query, collection, where, orderBy, limit as firestoreLimit, serverTimestamp, runTransaction, documentId } from 'firebase/firestore';
import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { generateTimestampId, generateTaskId } from './documentIdUtils';
import { hasPermission, FEATURE_PERMISSIONS } from './permissions';
import { logError, logUserAction } from './logger';

/**
 * Claim the next task number. `counters/taskNumber.next` is the NEXT number to use
 * (same contract as functions/utils/taskNumbers.js). Any number whose TSK-# doc already
 * exists is skipped, so a lagging counter can never overwrite an existing task.
 * @returns {Promise<number>}
 */
export async function allocateTaskNumber() {
  const counterRef = doc(db, collectionPath('counters'), 'taskNumber');
  for (let attempt = 0; attempt < 50; attempt++) {
    const n = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const next = snap.exists() ? (Number(snap.data()?.next) || 1) : 1;
      tx.set(counterRef, { next: next + 1 }, { merge: true });
      return next;
    });
    // List query (not a single-doc read) so non-admins aren't blocked by the tasks read rule
    const existing = await getDocs(query(
      collection(db, collectionPath('tasks')),
      where(documentId(), '==', generateTaskId(n)),
      firestoreLimit(1)
    ));
    if (existing.empty) return n;
  }
  throw new Error('Could not allocate a task number. Please try again or contact an administrator.');
}

/**
 * Generate next task number using counter transaction
 * Uses TSK-# format (e.g., TSK-1, TSK-984, TSK-47384)
 * @returns {Promise<{taskNumber: number, taskNumberLabel: string, taskDocId: string}>}
 */
async function getNextTaskNumber() {
  const taskNumber = await allocateTaskNumber();
  const taskDocId = generateTaskId(taskNumber); // TSK-# format
  return {
    taskNumber,
    taskNumberLabel: taskDocId, // Use same format for label
    taskDocId
  };
}

/**
 * Resolve entity associations for tasks/notes
 * Automatically populates parent entity IDs (e.g., Opportunity → Customer)
 * 
 * Use this when creating tasks or notes to automatically populate all related entities.
 * For example, a note on an opportunity will automatically include the customer association.
 * 
 * @param {Object} params - Entity identifiers
 * @param {string} [params.customerId] - direct customer ID
 * @param {string} [params.opportunityId] - Opportunity ID (will fetch customerId)
 * @param {string} [params.orderId] - Order ID (will fetch customerId)
 * @param {string} [params.contactId] - Contact ID (will fetch customerId)
 * @param {string} [params.taskId] - Task ID (for notes created on tasks)
 * @returns {Promise<Object>} - Complete association object with IDs and names
 * 
 * @example
 * // Create note on opportunity - automatically gets customer
 * const associations = await resolveAssociations({
 *   opportunityId: 'opp_123'
 * });
 * // Result: { customerId: 'customer_abc', customerName: 'Acme Corp', opportunityId: 'opp_123', ... }
 */
export async function resolveAssociations({
  customerId = null,
  opportunityId = null,
  orderId = null,
  contactId = null,
  taskId = null
} = {}) {
  const associations = {
    customerId: null,
    customerName: null,
    opportunityId: null,
    opportunityNumber: null,
    opportunityNumberLabel: null,
    orderId: null,
    orderNumber: null,
    contactId: null,
    contactName: null,
    taskId: null,
    taskNumberLabel: null
  };

  try {
    // Direct customer association
    if (customerId) {
      const customerDoc = await getDoc(doc(db, collectionPath('customers'), customerId));
      if (customerDoc.exists()) {
        associations.customerId = customerId;
        associations.customerName = customerDoc.data().customerName || null;
      }
    }

    // Opportunity → Customer (auto-populate)
    if (opportunityId) {
      const oppDoc = await getDoc(doc(db, collectionPath('opportunities'), opportunityId));
      if (oppDoc.exists()) {
        const oppData = oppDoc.data();
        associations.opportunityId = opportunityId;
        associations.opportunityNumber = oppData.opportunityNumber || null;
        // Document ID is the label (OPP-# format)
        associations.opportunityNumberLabel = oppData.opportunityNumberLabel || opportunityId;
        
        // Auto-populate customer from opportunity if not already set
        if (oppData.customerId && !associations.customerId) {
          associations.customerId = oppData.customerId;
          associations.customerName = oppData.customerName || null;
          
          // Fetch full customer name if not in opportunity data
          if (!associations.customerName && oppData.customerId) {
            const customerDoc = await getDoc(doc(db, collectionPath('customers'), oppData.customerId));
            if (customerDoc.exists()) {
              associations.customerName = customerDoc.data().customerName || null;
            }
          }
        }
      }
    }

    // Order → Customer (auto-populate)
    if (orderId) {
      const orderDoc = await getDoc(doc(db, collectionPath('orders'), orderId));
      if (orderDoc.exists()) {
        const orderData = orderDoc.data();
        associations.orderId = orderId;
        associations.orderNumber = orderData.orderNumber || orderData.order_number || orderData.number || null;
        
        // Auto-populate customer from order if not already set
        if (orderData.customerId && !associations.customerId) {
          associations.customerId = orderData.customerId;
          
          // Fetch customer name
          const customerDoc = await getDoc(doc(db, collectionPath('customers'), orderData.customerId));
          if (customerDoc.exists()) {
            associations.customerName = customerDoc.data().customerName || null;
          }
        } else if (orderData.customer_id && !associations.customerId) {
          // Fallback to customer_id field (ApparelMagic orders)
          associations.customerId = orderData.customer_id;
          
          const customerDoc = await getDoc(doc(db, collectionPath('customers'), orderData.customer_id));
          if (customerDoc.exists()) {
            associations.customerName = customerDoc.data().customerName || null;
          }
        }
      }
    }

    // Contact → Customer (auto-populate)
    // Contacts are now stored in standalone contacts collection
    // Document ID = ApparelMagic contact ID (e.g., "1761340645243")
    // Legacy format: "customerId_index" (e.g., "e7Vu9ul7maonau21tIOb_0")
    if (contactId) {
      try {
        const contactDoc = await getDoc(doc(db, collectionPath('contacts'), String(contactId)));
        if (contactDoc.exists()) {
          const contactData = contactDoc.data();
          associations.contactId = contactId;
          associations.contactName = contactData.fullName || 
            `${contactData.firstName || ''} ${contactData.lastName || ''}`.trim() || null;
          
          // Auto-populate customer from contact if not already set
          if (contactData.customerId && !associations.customerId) {
            associations.customerId = contactData.customerId;
            associations.customerName = contactData.customerName || null;
            
            // Fetch full customer name if not in contact data
            if (!associations.customerName && contactData.customerId) {
              const customerDoc = await getDoc(doc(db, collectionPath('customers'), contactData.customerId));
              if (customerDoc.exists()) {
                associations.customerName = customerDoc.data().customerName || null;
              }
            }
          }
        } else {
          // Fallback: legacy contactId format "customerId_index" (embedded in customer.contacts array)
          const legacyMatch = String(contactId).match(/^(.+)_(\d+)$/);
          if (legacyMatch) {
            const [, legacyCustId, legacyIdx] = legacyMatch;
            const custDoc = await getDoc(doc(db, collectionPath('customers'), legacyCustId));
            if (custDoc.exists()) {
              const custData = custDoc.data();
              const embeddedContacts = custData.contacts || [];
              const idx = parseInt(legacyIdx, 10);
              const legacyContact = embeddedContacts[idx];
              if (legacyContact) {
                associations.contactId = contactId;
                associations.contactName = legacyContact.fullName ||
                  `${legacyContact.firstName || ''} ${legacyContact.lastName || ''}`.trim() || null;
                // Auto-populate customer
                if (!associations.customerId) {
                  associations.customerId = legacyCustId;
                  associations.customerName = custData.customerName || null;
                }
              } else {
                console.warn(`Legacy contact index ${legacyIdx} out of bounds for customer ${legacyCustId}`);
              }
            } else {
              console.warn(`Contact not found (standalone or legacy): ${contactId}`);
            }
          } else {
            console.warn(`Contact not found: ${contactId}`);
          }
        }
      } catch (error) {
        console.error('Error resolving contact association:', error);
        // Continue execution, don't throw
      }
    }

    // Task → Customer/Opportunity (for notes created on tasks)
    if (taskId) {
      try {
        const taskDoc = await getDoc(doc(db, collectionPath('tasks'), taskId));
        if (taskDoc.exists()) {
          const taskData = taskDoc.data();
          associations.taskId = taskId;
          associations.taskNumberLabel = taskData.taskNumberLabel || taskId;
          
          // Get associations from task's associations object
          const taskAssoc = taskData.associations || {};
          
          // Auto-populate customer from task if not already set
          if (taskAssoc.customerId && !associations.customerId) {
            associations.customerId = taskAssoc.customerId;
            associations.customerName = taskAssoc.customerName || null;
            
            // Fetch full customer name if not in task data
            if (!associations.customerName && taskAssoc.customerId) {
              const customerDoc = await getDoc(doc(db, collectionPath('customers'), taskAssoc.customerId));
              if (customerDoc.exists()) {
                associations.customerName = customerDoc.data().customerName || null;
              }
            }
          }
          
          // Auto-populate opportunity from task if not already set
          if (taskAssoc.opportunityId && !associations.opportunityId) {
            associations.opportunityId = taskAssoc.opportunityId;
            associations.opportunityNumber = taskAssoc.opportunityNumber || null;
            associations.opportunityNumberLabel = taskAssoc.opportunityNumberLabel || taskAssoc.opportunityId;
          }
        } else {
          // Task not found, just set the ID
          associations.taskId = taskId;
        }
      } catch (error) {
        console.error('Error resolving task association:', error);
        associations.taskId = taskId;
      }
    }

    return associations;
    
  } catch (error) {
    await logError(error, {
      action: 'RESOLVE_ASSOCIATIONS',
      params: { customerId, opportunityId, orderId, contactId, taskId }
    });
    console.error('Error resolving associations:', {
      error: error.message,
      params: { customerId, opportunityId, orderId, contactId, taskId },
      timestamp: new Date().toISOString()
    });
    return associations; // Return partial data on error
  }
}

/**
 * Create a note with automatic cross-referencing
 * 
 * Creates a note in the unified notes collection with proper associations.
 * Automatically resolves parent entities (e.g., Opportunity → Customer).
 * Uses document ID naming standards: note_{sourceEntityType}_{sourceEntityId}_{YYYYMMDDTHHMMSS}
 * 
 * @param {Object} noteData - Note content and metadata
 * @param {string} noteData.text - Note text content (required)
 * @param {string} noteData.sourceEntityType - Where note was created: 'customer' | 'opportunity' | 'task' | 'order' | 'contact'
 * @param {string} noteData.sourceEntityId - Source entity ID
 * @param {string[]} [noteData.tags] - Optional tags for categorization
 * @param {boolean} [noteData.isPrivate] - Optional privacy flag (default: false)
 * @param {boolean} [noteData.pinned] - Optional pinning flag (default: false)
 * @param {Object} user - Current user object
 * @param {string} user.uid - User ID
 * @param {string} [user.displayName] - User display name
 * @param {string} [user.email] - User email
 * @param {Object} [associationParams] - Entity IDs for cross-reference resolution
 * @param {string} [associationParams.customerId] - customer ID
 * @param {string} [associationParams.opportunityId] - Opportunity ID
 * @param {string} [associationParams.orderId] - Order ID
 * @param {string} [associationParams.contactId] - Contact ID
 * @param {string} [associationParams.taskId] - Task ID
 * @returns {Promise<string>} - Created note document ID
 * 
 * @example
 * // Create note on opportunity (auto-links to customer)
 * const noteId = await createAssociatedNote(
 *   {
 *     text: 'Client wants custom pricing',
 *     sourceEntityType: 'opportunity',
 *     sourceEntityId: 'opp_123',
 *     tags: ['pricing', 'negotiation']
 *   },
 *   currentUser,
 *   { opportunityId: 'opp_123' }  // Will auto-resolve customer
 * );
 */
export async function createAssociatedNote(noteData, user, associationParams = {}) {
  try {
    // Validate required fields
    if (!noteData?.text?.trim()) {
      throw new Error('Note text is required');
    }
    if (!noteData.sourceEntityType) {
      throw new Error('Source entity type is required');
    }
    if (!noteData.sourceEntityId) {
      throw new Error('Source entity ID is required');
    }
    if (!user?.uid) {
      throw new Error('User is required');
    }

    // Resolve all associations (auto-populate parent entities)
    const associations = await resolveAssociations(associationParams);
    
    // Generate note ID following document ID standards
    // Format: note_{sourceEntityType}_{sourceEntityId}_{YYYYMMDDTHHMMSS}
    const noteId = generateTimestampId(`note_${noteData.sourceEntityType}_${noteData.sourceEntityId}`);
    
    // Resolve user display name
    const userName = user.displayName || user.email || user.name || 'Unknown';
    
    const note = {
      text: noteData.text.trim(),
      date: new Date().toISOString(),
      // Legacy field - kept for backwards compatibility
      user: userName,
      userId: user.uid,
      // Standard fields used by UnifiedNotes NoteCard
      createdBy: user.uid,
      createdByName: userName,
      sourceEntityType: noteData.sourceEntityType,
      sourceEntityId: noteData.sourceEntityId,
      associations,
      tags: noteData.tags || [],
      isPrivate: noteData.isPrivate || false,
      pinned: noteData.pinned || false,
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, collectionPath('notes'), noteId), note);
    
    // Log note creation
    await logUserAction({
      userId: user.uid,
      userEmail: user.email || null,
      userName: user.displayName || user.name || null,
      action: 'Note Created',
      details: `Note added to ${noteData.sourceEntityType} (${noteData.sourceEntityId}): "${noteData.text.trim().substring(0, 100)}${noteData.text.trim().length > 100 ? '...' : ''}"`,
      entityType: noteData.sourceEntityType === 'customer' ? 'Customer'
        : noteData.sourceEntityType === 'opportunity' ? 'Opportunity'
        : noteData.sourceEntityType === 'task' ? 'Task'
        : noteData.sourceEntityType === 'contact' ? 'Contact'
        : noteData.sourceEntityType === 'order' ? 'Order'
        : noteData.sourceEntityType,
      entityId: noteData.sourceEntityId,
      customerId: associations.customerId || null,
    });
    
    console.log('Note created with associations:', {
      noteId,
      sourceEntityType: noteData.sourceEntityType,
      sourceEntityId: noteData.sourceEntityId,
      associations,
      timestamp: new Date().toISOString()
    });
    
    return noteId;
    
  } catch (error) {
    await logError(error, {
      action: 'CREATE_ASSOCIATED_NOTE',
      sourceEntityType: noteData?.sourceEntityType,
      sourceEntityId: noteData?.sourceEntityId
    });
    console.error('Error creating associated note:', {
      error: error.message,
      noteData,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
}

/**
 * Query notes by entity with automatic cross-reference inclusion
 * 
 * Fetches all notes associated with an entity, including notes created on child entities.
 * For example, querying customer notes will also return notes from opportunities, orders, and contacts.
 * 
 * @param {string} entityType - Entity type: 'customer' | 'opportunity' | 'order' | 'contact' | 'task'
 * @param {string} entityId - Entity ID
 * @param {number} [limit=100] - Max results to return
 * @returns {Promise<Array>} - Array of note objects with metadata
 * 
 * @example
 * // Get all notes for a customer (includes notes from opportunities, orders, etc.)
 * const notes = await getNotesForEntity('customer', 'customer_abc123', 50);
 * // Returns notes with sourceEntityType badges for display
 */
export async function getNotesForEntity(entityType, entityId, limit = 100) {
  try {
    if (!entityType || !entityId) {
      throw new Error('Entity type and ID are required');
    }

    const fieldMap = {
      customer: 'associations.customerId',
      opportunity: 'associations.opportunityId',
      order: 'associations.orderId',
      contact: 'associations.contactId',
      task: 'associations.taskId'
    };
    
    const fieldPath = fieldMap[entityType];
    if (!fieldPath) {
      throw new Error(`Invalid entity type: ${entityType}. Must be one of: ${Object.keys(fieldMap).join(', ')}`);
    }
    
    const notesQuery = query(
      collection(db, collectionPath('notes')),
      where(fieldPath, '==', entityId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limit)
    );
    
    const snapshot = await getDocs(notesQuery);
    const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    console.log('Notes fetched for entity:', {
      entityType,
      entityId,
      count: notes.length,
      timestamp: new Date().toISOString()
    });
    
    return notes;
    
  } catch (error) {
    await logError(error, {
      action: 'GET_NOTES_FOR_ENTITY',
      entityType,
      entityId
    });
    console.error('Error getting notes for entity:', {
      error: error.message,
      entityType,
      entityId,
      timestamp: new Date().toISOString()
    });
    return []; // Return empty array on error
  }
}

/**
 * Delete a note (admin or creator only)
 * 
 * @param {string} noteId - Note document ID
 * @param {Object} user - Current user
 * @returns {Promise<void>}
 */
export async function deleteNote(noteId, user) {
  try {
    const { deleteDoc } = await import('firebase/firestore');
    
    // Verify ownership or admin
    const noteDoc = await getDoc(doc(db, collectionPath('notes'), noteId));
    
    if (!noteDoc.exists()) {
      throw new Error('Note not found');
    }
    
    const noteData = noteDoc.data();
    const isOwner = noteData.userId === user.uid || noteData.createdBy === user.uid;
    const canDeleteAny = hasPermission(user, FEATURE_PERMISSIONS.NOTE_DELETE_ANY);
    
    if (!isOwner && !canDeleteAny) {
      throw new Error('Permission denied: You can only delete your own notes');
    }
    
    await deleteDoc(doc(db, collectionPath('notes'), noteId));
    
    // Log note deletion
    await logUserAction({
      userId: user.uid,
      userEmail: user.email || null,
      userName: user.displayName || user.name || null,
      action: 'Note Deleted',
      details: `Note deleted from ${noteData.sourceEntityType || 'unknown'} (${noteData.sourceEntityId || noteId}): "${(noteData.text || '').substring(0, 100)}${(noteData.text || '').length > 100 ? '...' : ''}" (created by ${noteData.createdByName || noteData.user || 'unknown'})`,
      entityType: noteData.sourceEntityType === 'customer' ? 'Customer'
        : noteData.sourceEntityType === 'opportunity' ? 'Opportunity'
        : noteData.sourceEntityType === 'task' ? 'Task'
        : noteData.sourceEntityType === 'contact' ? 'Contact'
        : noteData.sourceEntityType === 'order' ? 'Order'
        : noteData.sourceEntityType || 'Note',
      entityId: noteData.sourceEntityId || noteId,
      customerId: noteData.associations?.customerId || null,
    });
    
    console.log('Note deleted:', {
      noteId,
      deletedBy: user.uid,
      timestamp: new Date().toISOString()
    });
    
  } catch (error) {
    await logError(error, {
      action: 'DELETE_NOTE',
      noteId
    });
    console.error('Error deleting note:', {
      error: error.message,
      noteId,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
}

/**
 * Get tasks associated with an entity
 * 
 * @param {string} entityType - Entity type: 'customer' | 'opportunity' | 'order' | 'contact'
 * @param {string} entityId - Entity ID
 * @param {number} [limit=50] - Max results to return
 * @returns {Promise<Array>} - Array of task objects
 * 
 * @example
 * // Get all tasks for a customer
 * const tasks = await getTasksForEntity('customer', 'customer_abc123');
 */
export async function getTasksForEntity(entityType, entityId, limit = 50) {
  try {
    if (!entityType || !entityId) {
      throw new Error('Entity type and ID are required');
    }

    const fieldMap = {
      customer: 'associations.customerId',
      opportunity: 'associations.opportunityId',
      order: 'associations.orderId',
      contact: 'associations.contactId'
    };
    
    const fieldPath = fieldMap[entityType];
    if (!fieldPath) {
      throw new Error(`Invalid entity type: ${entityType}`);
    }
    
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where(fieldPath, '==', entityId),
      orderBy('dueDate', 'asc'),
      firestoreLimit(limit)
    );
    
    const snapshot = await getDocs(tasksQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
  } catch (error) {
    await logError(error, {
      action: 'GET_TASKS_FOR_ENTITY',
      entityType,
      entityId
    });
    console.error('Error getting tasks for entity:', {
      error: error.message,
      entityType,
      entityId,
      timestamp: new Date().toISOString()
    });
    return [];
  }
}

/**
 * Create a task with associations
 * Uses TSK-# format (e.g., TSK-1, TSK-984, TSK-47384)
 * 
 * @param {Object} taskData - Task data
 * @param {Object} associationParams - Entity associations
 * @param {Object} user - Current user
 * @returns {Promise<string>} - Created task ID (taskDocId like TSK-984)
 */
export async function createAssociatedTask(taskData, associationParams, user) {
  try {
    // Generate task number using counter (same as TaskPage.jsx)
    const { taskNumber, taskNumberLabel, taskDocId } = await getNextTaskNumber();
    
    // Resolve associations
    const associations = await resolveAssociations(associationParams);
    
    // Determine primary entity
    let primaryEntityType = 'standalone';
    let primaryEntityId = null;
    
    if (associationParams.opportunityId) {
      primaryEntityType = 'opportunity';
      primaryEntityId = associationParams.opportunityId;
    } else if (associationParams.orderId) {
      primaryEntityType = 'order';
      primaryEntityId = associationParams.orderId;
    } else if (associationParams.contactId) {
      primaryEntityType = 'contact';
      primaryEntityId = associationParams.contactId;
    } else if (associationParams.customerId) {
      primaryEntityType = 'customer';
      primaryEntityId = associationParams.customerId;
    }
    
    const task = {
      ...taskData,
      taskNumber,
      taskNumberLabel,
      associations,
      primaryEntityType,
      primaryEntityId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Use setDoc with custom document ID (T000116 format)
    await setDoc(doc(db, collectionPath('tasks'), taskDocId), task);
    
    // Log task creation
    await logUserAction({
      userId: user.uid,
      userEmail: user.email || null,
      userName: user.displayName || user.name || null,
      action: 'Task Created',
      details: `Task ${taskNumberLabel} created via association: "${taskData.title || taskData.subject || ''}"`,
      entityType: 'Task',
      entityId: taskDocId,
      customerId: associations.customerId || null,
    });
    
    console.log('Task created with associations:', {
      taskId: taskDocId,
      taskNumberLabel,
      primaryEntityType,
      associations,
      timestamp: new Date().toISOString()
    });
    
    return taskDocId;
    
  } catch (error) {
    await logError(error, {
      action: 'CREATE_ASSOCIATED_TASK',
      associationParams
    });
    console.error('Error creating associated task:', {
      error: error.message,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
}

/**
 * Fetch tasks by entity ID
 * Queries the associations object for entity references
 * 
 * @param {string} entityType - 'customer' | 'opportunity' | 'order' | 'contact'
 * @param {string} entityId - The entity ID to search for
 * @param {number} maxResults - Maximum number of tasks to return (default: 100)
 * @returns {Promise<Array>} - Array of task objects sorted by dueDate
 * 
 * @example
 * const tasks = await fetchTasksByEntity('customer', 'comp_123');
 * const oppTasks = await fetchTasksByEntity('opportunity', 'OPP-41');
 */
export async function fetchTasksByEntity(entityType, entityId, maxResults = 100) {
  if (!entityType || !entityId) return [];
  
  // Map entity type to field names
  const fieldMap = {
    customer: 'customerId',
    opportunity: 'opportunityId',
    order: 'orderId',
    contact: 'contactId'
  };
  
  const fieldName = fieldMap[entityType];
  if (!fieldName) {
    console.warn(`Unknown entity type: ${entityType}`);
    return [];
  }
  
  try {
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where(`associations.${fieldName}`, '==', entityId),
      orderBy('dueDate', 'asc'),
      firestoreLimit(maxResults)
    );
    
    const snapshot = await getDocs(tasksQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error fetching tasks for ${entityType}:`, error);
    return [];
  }
}

/**
 * Fetch ALL notes related to an opportunity (unified view)
 * Queries notes from: opportunity directly + all associated tasks
 * 
 * This provides a complete view of an opportunity by:
 * 1. Fetching notes directly associated with the opportunity
 * 2. Fetching all tasks for the opportunity, then their notes
 * 
 * @param {string} opportunityId - The opportunity ID
 * @param {number} maxNotesPerEntity - Max notes per entity type (default: 50)
 * @returns {Promise<Array>} - Array of note objects sorted by createdAt desc
 */
export async function fetchAllOpportunityNotes(opportunityId, maxNotesPerEntity = 50) {
  if (!opportunityId) return [];
  
  const allNotes = new Map(); // Use Map to deduplicate by note ID
  
  try {
    // 1. Direct opportunity notes
    const oppNotesQuery = query(
      collection(db, collectionPath('notes')),
      where('associations.opportunityId', '==', opportunityId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxNotesPerEntity)
    );
    
    // 2. Get opportunity's tasks
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('associations.opportunityId', '==', opportunityId),
      firestoreLimit(100)
    );
    
    // Execute queries in parallel
    const [oppNotesSnap, tasksSnap] = await Promise.all([
      getDocs(oppNotesQuery),
      getDocs(tasksQuery)
    ]);
    
    // Add direct opportunity notes
    oppNotesSnap.docs.forEach(doc => {
      allNotes.set(doc.id, { id: doc.id, ...doc.data() });
    });
    
    // Get task IDs
    const taskIds = tasksSnap.docs.map(d => d.id);
    
    // Fetch notes for each task from the unified notes collection
    // Task notes use associations.taskId field
    if (taskIds.length > 0) {
      const taskNoteQueries = taskIds.map(taskId =>
        getDocs(query(
          collection(db, collectionPath('notes')),
          where('associations.taskId', '==', taskId),
          firestoreLimit(maxNotesPerEntity)
        ))
      );
      
      const taskNoteSnapshots = await Promise.all(taskNoteQueries);
      
      // Add all task notes to the map (deduplicates automatically)
      taskNoteSnapshots.forEach((snap, idx) => {
        snap.docs.forEach(doc => {
          allNotes.set(doc.id, { id: doc.id, ...doc.data() });
        });
      });
    }
    
    // Convert to array and sort by createdAt desc
    const notesArray = Array.from(allNotes.values());
    notesArray.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
      const bTime = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
      return bTime - aTime; // Descending
    });
    
    return notesArray;
    
  } catch (error) {
    console.error('Error fetching all opportunity notes:', error);
    return [];
  }
}

/**
 * Fetch ALL notes related to a customer (unified view)
 * Queries notes from: customer, opportunities, tasks, orders, contacts
 * 
 * This provides a complete activity view for a customer by:
 * 1. Fetching notes directly associated with the customer
 * 2. Fetching all opportunities for the customer, then their notes
 * 3. Fetching all tasks for the customer, then their notes
 * 4. Fetching all orders for the customer, then their notes
 * 5. Fetching all contacts for the customer, then their notes
 * 
 * @param {string} customerId - The customer ID
 * @param {number} maxNotesPerEntity - Max notes per entity type (default: 50)
 * @returns {Promise<Array>} - Array of note objects sorted by createdAt desc
 */
export async function fetchAllCustomerNotes(customerId, maxNotesPerEntity = 50) {
  if (!customerId) return [];
  
  const allNotes = new Map(); // Use Map to deduplicate by note ID
  
  try {
    // 1. Direct customer notes
    const customerNotesQuery = query(
      collection(db, collectionPath('notes')),
      where('associations.customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxNotesPerEntity)
    );
    
    // 2. Get customer's opportunities
    const oppsQuery = query(
      collection(db, collectionPath('opportunities')),
      where('customerId', '==', customerId),
      firestoreLimit(100)
    );
    
    // 3. Get customer's tasks (via associations)
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('associations.customerId', '==', customerId),
      firestoreLimit(100)
    );
    
    // 4. Get customer's orders
    const ordersQuery = query(
      collection(db, collectionPath('orders')),
      where('customer_id', '==', customerId),
      firestoreLimit(100)
    );
    
    // 5. Get customer's contacts
    const contactsQuery = query(
      collection(db, collectionPath('contacts')),
      where('customerId', '==', customerId),
      firestoreLimit(100)
    );
    
    // Execute all entity queries in parallel
    const [customerNotesSnap, oppsSnap, tasksSnap, ordersSnap, contactsSnap] = await Promise.all([
      getDocs(customerNotesQuery),
      getDocs(oppsQuery),
      getDocs(tasksQuery),
      getDocs(ordersQuery),
      getDocs(contactsQuery)
    ]);
    
    // Add direct customer notes
    customerNotesSnap.docs.forEach(doc => {
      allNotes.set(doc.id, { id: doc.id, ...doc.data() });
    });
    
    // Build arrays of related entity IDs
    const oppIds = oppsSnap.docs.map(d => d.id);
    let taskIds = tasksSnap.docs.map(d => d.id);
    const orderIds = ordersSnap.docs.map(d => d.id);
    const contactIds = contactsSnap.docs.map(d => d.id);
    
    // Also fetch tasks linked to the customer's opportunities (for tasks not directly tagged with customerId)
    // This handles cases like auto-created tasks linked via opportunity
    if (oppIds.length > 0) {
      const oppTaskQueries = oppIds.map(oppId => 
        getDocs(query(
          collection(db, collectionPath('tasks')),
          where('associations.opportunityId', '==', oppId),
          firestoreLimit(50)
        ))
      );
      const oppTaskSnapshots = await Promise.all(oppTaskQueries);
      oppTaskSnapshots.forEach((snap, idx) => {
        snap.docs.forEach(doc => {
          if (!taskIds.includes(doc.id)) {
            taskIds.push(doc.id);
          }
        });
      });
    }
    
    // Fetch notes for each entity type (in parallel batches)
    const noteQueries = [];
    
    // Opportunity notes
    for (const oppId of oppIds) {
      noteQueries.push(
        getDocs(query(
          collection(db, collectionPath('notes')),
          where('associations.opportunityId', '==', oppId),
          firestoreLimit(maxNotesPerEntity)
        ))
      );
    }
    
    // Task notes
    for (const taskId of taskIds) {
      noteQueries.push(
        getDocs(query(
          collection(db, collectionPath('notes')),
          where('associations.taskId', '==', taskId),
          firestoreLimit(maxNotesPerEntity)
        ))
      );
    }
    
    // Order notes
    for (const orderId of orderIds) {
      noteQueries.push(
        getDocs(query(
          collection(db, collectionPath('notes')),
          where('associations.orderId', '==', orderId),
          firestoreLimit(maxNotesPerEntity)
        ))
      );
    }
    
    // Contact notes
    for (const contactId of contactIds) {
      noteQueries.push(
        getDocs(query(
          collection(db, collectionPath('notes')),
          where('associations.contactId', '==', contactId),
          firestoreLimit(maxNotesPerEntity)
        ))
      );
    }
    
    // Execute all note queries
    const noteSnapshots = await Promise.all(noteQueries);
    
    // Add all notes to the map (deduplicates automatically)
    let queryIdx = 0;
    
    // Add opportunity notes
    for (let i = 0; i < oppIds.length; i++) {
      noteSnapshots[queryIdx].docs.forEach(doc => {
        allNotes.set(doc.id, { id: doc.id, ...doc.data() });
      });
      queryIdx++;
    }
    
    // Add task notes  
    for (let i = 0; i < taskIds.length; i++) {
      noteSnapshots[queryIdx].docs.forEach(doc => {
        allNotes.set(doc.id, { id: doc.id, ...doc.data() });
      });
      queryIdx++;
    }
    
    // Add remaining order/contact notes
    for (let i = queryIdx; i < noteSnapshots.length; i++) {
      noteSnapshots[i].docs.forEach(doc => {
        allNotes.set(doc.id, { id: doc.id, ...doc.data() });
      });
    }
    
    // Convert to array and sort by createdAt desc
    const notesArray = Array.from(allNotes.values());
    notesArray.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
      const bTime = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
      return bTime - aTime; // Descending
    });
    
    return notesArray;
    
  } catch (error) {
    console.error('Error fetching all customer notes:', error);
    return [];
  }
}

/**
 * Fetch ALL activity logs related to a customer (unified view)
 * Queries logs from: logsCustomer, logsOpp, logsTask, logsOrder, logsContact
 * 
 * This provides a complete activity history for a customer by:
 * 1. Fetching logs directly associated with the customer (logsCustomer)
 * 2. Fetching all opportunities for the customer, then their logs (logsOpp)
 * 3. Fetching all tasks for the customer, then their logs (logsTask)
 * 4. Fetching all orders for the customer, then their logs (logsOrder)
 * 5. Fetching all contacts for the customer, then their logs (logsContact)
 * 
 * @param {string} customerId - The customer ID
 * @param {number} maxLogsPerEntity - Max logs per entity type (default: 50)
 * @returns {Promise<Array>} - Array of log objects sorted by timestamp desc
 */
export async function fetchAllCustomerLogs(customerId, maxLogsPerEntity = 50) {
  if (!customerId) return [];
  
  const allLogs = new Map(); // Use Map to deduplicate by log ID
  
  try {
    // 1. Direct customer logs - filter client-side since entityId may be in different fields
    const customerLogsQuery = query(
      collection(db, collectionPath('logsCustomer')),
      orderBy('timestamp', 'desc'),
      firestoreLimit(200) // Fetch more, filter client-side
    );
    
    // 2. Get customer's opportunities
    const oppsQuery = query(
      collection(db, collectionPath('opportunities')),
      where('customerId', '==', customerId),
      firestoreLimit(100)
    );
    
    // 3. Get customer's tasks (via associations)
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('associations.customerId', '==', customerId),
      firestoreLimit(100)
    );
    
    // 4. Get customer's orders
    const ordersQuery = query(
      collection(db, collectionPath('orders')),
      where('customer_id', '==', customerId),
      firestoreLimit(100)
    );
    
    // 5. Get customer's contacts
    const contactsQuery = query(
      collection(db, collectionPath('contacts')),
      where('customerId', '==', customerId),
      firestoreLimit(100)
    );
    
    // Execute all entity queries in parallel
    const [customerLogsSnap, oppsSnap, tasksSnap, ordersSnap, contactsSnap] = await Promise.all([
      getDocs(customerLogsQuery),
      getDocs(oppsQuery),
      getDocs(tasksQuery),
      getDocs(ordersQuery),
      getDocs(contactsQuery)
    ]);
    
    // Add direct customer logs (filter to this customer)
    customerLogsSnap.docs.forEach(doc => {
      const data = doc.data();
      // Check various field names for customer ID
      if (
        data.customerId === customerId ||
        data.customerId === customerId ||
        data.entityId === customerId ||
        (data.entityType === 'Customer' && data.entityId === customerId)
      ) {
        allLogs.set(doc.id, { 
          id: doc.id, 
          ...data,
          _logSource: 'customer' // Mark the source for UI grouping
        });
      }
    });
    
    // Build arrays of related entity IDs and create lookup maps
    const oppIds = oppsSnap.docs.map(d => d.id);
    const oppMap = new Map(oppsSnap.docs.map(d => [d.id, d.data()]));
    
    let taskIds = tasksSnap.docs.map(d => d.id);
    let taskMap = new Map(tasksSnap.docs.map(d => [d.id, d.data()]));
    
    // Also fetch tasks linked to the customer's opportunities (for tasks not directly tagged with customerId)
    if (oppIds.length > 0) {
      const oppTaskQueries = oppIds.map(oppId => 
        getDocs(query(
          collection(db, collectionPath('tasks')),
          where('associations.opportunityId', '==', oppId),
          firestoreLimit(50)
        ))
      );
      const oppTaskSnapshots = await Promise.all(oppTaskQueries);
      oppTaskSnapshots.forEach(snap => {
        snap.docs.forEach(doc => {
          if (!taskIds.includes(doc.id)) {
            taskIds.push(doc.id);
            taskMap.set(doc.id, doc.data());
          }
        });
      });
    }
    
    const orderIds = ordersSnap.docs.map(d => d.id);
    const orderMap = new Map(ordersSnap.docs.map(d => [d.id, d.data()]));
    
    const contactIds = contactsSnap.docs.map(d => d.id);
    const contactMap = new Map(contactsSnap.docs.map(d => [d.id, d.data()]));
    
    // Fetch logs for each entity type
    // Since logs don't have indexed entityId fields, we fetch all and filter client-side
    const logQueries = [];
    
    // Opportunity logs
    if (oppIds.length > 0) {
      logQueries.push(
        getDocs(query(
          collection(db, collectionPath('logsOpp')),
          orderBy('timestamp', 'desc'),
          firestoreLimit(maxLogsPerEntity * oppIds.length)
        )).then(snap => ({
          type: 'opportunity',
          docs: snap.docs,
          entityIds: oppIds,
          entityMap: oppMap
        }))
      );
    }
    
    // Task logs
    if (taskIds.length > 0) {
      logQueries.push(
        getDocs(query(
          collection(db, collectionPath('logsTask')),
          orderBy('timestamp', 'desc'),
          firestoreLimit(maxLogsPerEntity * taskIds.length)
        )).then(snap => ({
          type: 'task',
          docs: snap.docs,
          entityIds: taskIds,
          entityMap: taskMap
        }))
      );
    }
    
    // Order logs
    if (orderIds.length > 0) {
      logQueries.push(
        getDocs(query(
          collection(db, collectionPath('logsOrder')),
          orderBy('timestamp', 'desc'),
          firestoreLimit(maxLogsPerEntity * orderIds.length)
        )).then(snap => ({
          type: 'order',
          docs: snap.docs,
          entityIds: orderIds,
          entityMap: orderMap
        }))
      );
    }
    
    // Contact logs
    if (contactIds.length > 0) {
      logQueries.push(
        getDocs(query(
          collection(db, collectionPath('logsContact')),
          orderBy('timestamp', 'desc'),
          firestoreLimit(maxLogsPerEntity * contactIds.length)
        )).then(snap => ({
          type: 'contact',
          docs: snap.docs,
          entityIds: contactIds,
          entityMap: contactMap
        }))
      );
    }
    
    // Execute all log queries
    const logResults = await Promise.all(logQueries);
    
    // Process each log type and filter to relevant entities
    logResults.forEach(result => {
      result.docs.forEach(doc => {
        const data = doc.data();
        const entityId = data.entityId || data.opportunityId || data.taskId || data.orderId || data.contactId;
        
        // Only include if the log is for one of our related entities
        if (result.entityIds.includes(entityId)) {
          const entity = result.entityMap.get(entityId);
          allLogs.set(doc.id, { 
            id: doc.id, 
            ...data,
            _logSource: result.type,
            _entityLabel: getEntityLabel(result.type, entity)
          });
        }
      });
    });
    
    // Convert to array and sort by timestamp desc
    const logsArray = Array.from(allLogs.values());
    logsArray.sort((a, b) => {
      const aTime = a.timestamp?.toDate?.() || a.timestamp || new Date(0);
      const bTime = b.timestamp?.toDate?.() || b.timestamp || new Date(0);
      return bTime - aTime; // Descending
    });
    
    return logsArray;
    
  } catch (error) {
    console.error('Error fetching all customer logs:', error);
    return [];
  }
}

/**
 * Get a human-readable label for an entity
 * @private
 */
function getEntityLabel(entityType, entity) {
  if (!entity) return null;
  
  switch (entityType) {
    case 'opportunity':
      return entity.opportunityNumberLabel || `OPP-${entity.opportunityNumber}` || entity.leadSource;
    case 'task':
      return entity.taskNumberLabel || `TSK-${entity.taskNumber}` || entity.subject;
    case 'order':
      return entity.order_number || entity.orderNumber || entity.id;
    case 'contact':
      return `${entity.firstName || ''} ${entity.lastName || ''}`.trim() || entity.email;
    default:
      return null;
  }
}

/**
 * Fetch open automated tasks for an opportunity's current stage
 * Used to warn users before advancing stages if tasks are incomplete
 * 
 * @param {string} opportunityId - The opportunity ID (document ID)
 * @param {string} currentStage - The current stage to check for open tasks
 * @returns {Promise<{hasOpenTasks: boolean, openTasks: Array, count: number}>}
 * 
 * @example
 * const result = await getOpenTasksForOpportunityStage('OPP-123', 'Qualified');
 * if (result.hasOpenTasks) {
 *   // Warn user about open tasks before allowing stage change
 * }
 */
export async function getOpenTasksForOpportunityStage(opportunityId, currentStage) {
  if (!opportunityId || !currentStage) {
    return { hasOpenTasks: false, openTasks: [], count: 0 };
  }
  
  const automationSource = `stage_${currentStage.toLowerCase().replace(/\s+/g, '_')}`;
  
  try {
    // Query for tasks linked to this opportunity
    // Use simple query and filter in code to avoid composite index requirements
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('associations.opportunityId', '==', opportunityId),
      firestoreLimit(100)
    );
    
    const snapshot = await getDocs(tasksQuery);
    
    // Filter in code: open tasks with matching automationSource
    const openTasks = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(task => 
        task.status === 'Open' && 
        task.automationSource === automationSource
      )
      .map(task => ({
        ...task,
        taskLabel: task.taskNumberLabel || `TSK-${task.taskNumber}` || task.id
      }));
    
    console.log(`[getOpenTasksForOpportunityStage] Found ${snapshot.docs.length} tasks for opp ${opportunityId}, ${openTasks.length} open for stage '${currentStage}' (automationSource: ${automationSource})`);
    
    return {
      hasOpenTasks: openTasks.length > 0,
      openTasks,
      count: openTasks.length
    };
  } catch (error) {
    console.error('Error checking open tasks for stage:', error);
    // Don't block stage changes on error - just log and continue
    return { hasOpenTasks: false, openTasks: [], count: 0, error: error.message };
  }
}

/**
 * Fetch ALL open tasks for an opportunity (any stage)
 * Useful for checking before closing an opportunity
 * 
 * @param {string} opportunityId - The opportunity ID (document ID)
 * @returns {Promise<{hasOpenTasks: boolean, openTasks: Array, count: number}>}
 */
export async function getAllOpenTasksForOpportunity(opportunityId) {
  if (!opportunityId) {
    return { hasOpenTasks: false, openTasks: [], count: 0 };
  }
  
  try {
    const tasksQuery = query(
      collection(db, collectionPath('tasks')),
      where('associations.opportunityId', '==', opportunityId),
      where('status', '==', 'Open'),
      firestoreLimit(50)
    );
    
    const snapshot = await getDocs(tasksQuery);
    const openTasks = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      taskLabel: doc.data().taskNumberLabel || `TSK-${doc.data().taskNumber}` || doc.id
    }));
    
    return {
      hasOpenTasks: openTasks.length > 0,
      openTasks,
      count: openTasks.length
    };
  } catch (error) {
    console.error('Error checking open tasks for opportunity:', error);
    return { hasOpenTasks: false, openTasks: [], count: 0, error: error.message };
  }
}
